import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { GlobalToken, Popover, theme } from 'antd';
import styled from 'styled-components';

import { useStores } from '@/hooks/use-stores';
import { ComponentTierName } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import EPCheckbox from '@/comp/EPCheckbox';
import { EPButton } from '@/components';

import ArchiveIcon from '../common/icons/ArchiveIcon';

interface AlertListWithSelectionProps {
  children: JSX.Element;
  alerts: JSX.Element[];
  setOpenAlertId: (id: string) => void;
  openAlertId: string;
}

const { useToken } = theme;

const AlertListWithSelection = observer(({ children, alerts }: AlertListWithSelectionProps) => {
  const { t } = useTranslation();
  const [isPartSelected, setIsPartSelected] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const { alertStore, stationStore } = useStores();
  const { token } = useToken();
  const shouldRestrictAlerts = checkTierPermission(ComponentTierName.PaymentPlan, stationStore);

  const selectClick = () => {
    if (alertStore.selectedAlerts.length === alerts.length) {
      alertStore.resetSelectedAlerts();
    } else {
      alerts.forEach(alert => {
        if (!alert.props.alert.isSelected) {
          alert.props.alert.isSelected = true;
          alertStore.addSelectedAlert(alert.props.alert);
        }
      });
    }
  };

  const onClickArchive = async () => {
    await alertStore.archiveBatch();
  };

  useEffect(() => {
    if (alertStore.selectedAlerts.length === 0) {
      setIsPartSelected(false);
      setIsAllSelected(false);
    } else if (alertStore.selectedAlerts.length === alerts.length) {
      setIsPartSelected(false);
      setIsAllSelected(true);
    } else {
      setIsPartSelected(true);
      setIsAllSelected(false);
    }
  }, [alertStore.selectedAlerts.length]);

  return (
    <AlertListWithSelectionContainer>
      <>
        <div className="all-selection">
          {!shouldRestrictAlerts && (
            <EPCheckbox checked={isAllSelected} indeterminate={isPartSelected} onChange={selectClick}>
              {t('Select All')}
            </EPCheckbox>
          )}
          {(isAllSelected || isPartSelected) && (
            <Popover placement="bottom" content={<span>{t('Archive Selected Alerts')}</span>}>
              <EPButton
                type="link"
                size="small"
                className="icon"
                icon={<ArchiveIcon color={token.colorTextTertiary} />}
                onClick={onClickArchive}
              />
            </Popover>
          )}
        </div>
        {alerts}
        {children}
      </>
    </AlertListWithSelectionContainer>
  );
});

const AlertListWithSelectionContainer = styled.div<{ theme: { token: GlobalToken } }>`
  .ant-checkbox-inner {
    background-color: ${({ theme: { token } }) => token.colorBgContainer};
    border: 1px solid;
    border-color: ${({ theme: { token } }) => token.colorBorder};
    border-radius: 3px;
  }
`;
export default AlertListWithSelection;
